import { TextArea, Input, Text } from '@opendoor/bricks/core';
import { useState } from 'react';
import { BaseQuestion } from 'sellDirect/components/closingDash/disclosures/DynamicDisclosures/BaseQuestion';
import {
  Prompt,
  PromptResponse,
  TextResponseType,
  isValidYearOrUnknown,
} from 'sellDirect/helpers/closingDash/disclosures';

export interface TextQuestionProps {
  question: Prompt;
  onResponseChange: (value: PromptResponse | null) => void;
  showErrors?: boolean;
}

/**
 * Creates text question component
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  onResponseChange,
  showErrors,
}) => {
  const getErrorMessagePanel = (value: string | undefined) => {
    let errorMessage = '';
    if (!value) {
      errorMessage = 'Required';
    } else if (
      question.displayText.toLowerCase().includes('year') &&
      !isValidYearOrUnknown(value)
    ) {
      errorMessage = "Please enter year between 1850 and 2100 or unknown if you don't know.";
    }

    return errorMessage && <Text color="red50">{errorMessage}</Text>;
  };

  const initialResponse = (question.response as TextResponseType)?.text;
  const [response, setResponse] = useState<string | undefined>(initialResponse);

  const onUpdate = (value?: string) => {
    setResponse(value);

    const response: TextResponseType | null = value
      ? {
          text: value,
          __typename: 'OdProtosRubricV2Data_Prompt_ResponseOneof_Text',
        }
      : null;
    onResponseChange(response);
  };

  const errorMessagePanel = getErrorMessagePanel(response);

  // Create a text box and update response if there
  const textComponent =
    question.id === 'open_question' ? (
      <TextArea
        aria-labelledby={question.id}
        analyticsName={`question-${question.id}`}
        key={question.id}
        id={question.id}
        onChange={(_event, _name, value) => onUpdate(value?.toString())}
        unboundedWidth={true}
        required={showErrors && !!errorMessagePanel}
        content={response}
      />
    ) : (
      <Input
        aria-labelledby={question.id}
        analyticsName={`question-${question.id}`}
        type="text"
        key={question.id}
        id={question.id}
        title={question.displayText}
        value={response}
        onChange={(_id, value) => onUpdate(value)}
        unboundedWidth={true}
        aria-invalid={showErrors && !!errorMessagePanel}
      />
    );
  const component = (
    <BaseQuestion question={question}>
      {textComponent}
      {showErrors && errorMessagePanel}
    </BaseQuestion>
  );
  return component;
};
